/**
 * Status auras — what is currently true about a body, drawn ON the body, in
 * the pass-2 language.
 *
 * ── The gap this closes ───────────────────────────────────────────────────
 *
 * `PlayerSnapshot.effects` carries every active effect by name. Pass 1 drew
 * exactly one of them, ranked, as a flat ring at the feet: a stun, a slow, a
 * shield and a speed buff were the same hoop in four colours, invisible over a
 * bright deck from the 55° camera, and the founder's verdict on the shield
 * was "sucks". A status has to be readable at a glance from across the arena
 * — WHAT is true about that body — so each one is a drawn OBJECT in its own
 * place on the body, and several can be true at once:
 *
 *   shield      damage_reduction   a chunky faceted cel shell around the body
 *                                  (Walled Garden); ripples when struck,
 *                                  erodes away when it expires
 *   stun        stun               three cartoon stars orbiting the head
 *   slow        slow               an ice crust at the feet, ice specks
 *   suppressed  suppress_abilities inside Deindexed: red ticks drained off the
 *                                  body over a broken red ring (the zone's
 *                                  damage is applied directly; the sim never
 *                                  puts `damage_over_time` on the wire)
 *   speed       speed              a spinning hex glyph at the feet and
 *                                  electric ticks off the body (Overclock,
 *                                  Ludicrous Mode)
 *   faded       invisible          a shimmer ring at the feet — only your own
 *                                  body ever carries it, the server omits
 *                                  faded opponents from the snapshot
 *   hover       hover              a thruster exhaust under the body, puffs
 *                                  falling away, and a blob shadow on the deck
 *                                  it took off from (Hover Slam)
 *
 * Effects that are pure sim bookkeeping — `busy`, `iframe`, `dash`,
 * `slow_immune`, `no_dash`, `pull` — are NOT drawn. `busy` is true during the
 * recovery of every cast; a ring that is on almost always says nothing.
 *
 * Colours: the identity-coloured ones (shield, speed, faded) take the body's
 * family colour through CelLook's identity(); their floor-drawn parts take
 * the character's `telegraphHex`. Debuffs are fixed colours, because a debuff
 * is information about a threat and must mean the same thing whoever wears
 * it: stun amber, slow ice, suppression hazard red. Never purple.
 *
 * Every shape is a pooled CelShape (acquired on the frame the effect appears,
 * released when its erode-out ends), so a status costs no allocation and its
 * programs are compiled by the prewarm, never mid-fight.
 */

import * as THREE from 'three/webgpu';
import { PLAYER_HALF_HEIGHT } from '@chaos/sim';
import { CHARACTERS, telegraphHex } from '@chaos/protocol';
import { DECAL_LIFT } from './decals.js';
import { hexShield, iceCrust, hazeRing } from '../vfx/StatusShapes.js';
import { column } from '../vfx/ZoneShapes.js';
import { starFlash, hexRing, boltBeam, releaseCelShape } from '../vfx/CelShapes.js';
import { popScale, erosionAt } from '../vfx/CelLook.js';
import { getTierConfig } from '../PostProcessing.js';
import { registerDiagnostics } from '../performance/runtime.js';
import * as fx from '../vfx/fx.js';
import { frame } from '../state.js';

/** Bodies with a live aura, for the probe (`chaosPerf.stats().auras`). */
const _bodies = new Set();
registerDiagnostics('auras', () => [..._bodies].map((g) => ({
  id: g.userData?.characterId ?? null,
  slots: statusAuraSlots(g),
  at: [+g.position.x.toFixed(1), +g.position.y.toFixed(1), +g.position.z.toFixed(1)],
  visible: g.visible,
  parts: [...(g.userData?.auras?.values() ?? [])].flatMap((s) => s.parts.map((m) => [m.type, +m.scale.x.toFixed(2), m.visible, +m.position.y.toFixed(2)])),
})));

/** Debuff colours. Fixed, whoever wears them. */
export const STUN_HEX = 0xffc107;
/** Ice: deeper than the old ring's 0x9ad8ff so the crust reads as blue, not white, on the teal deck. */
export const SLOW_HEX = 0x74c6ff;
export const DRAIN_HEX = 0xff3d3d;

/** Where the body's origin sits above its feet — a capsule fact, not a taste. */
const FOOT_OFFSET = -PLAYER_HALF_HEIGHT;
/** The pop-in on appearance, and the erode-out on expiry. */
const POP_MS = 170;
const END_MS = 260;
/** The shield's ripple runs over the shell in this long. */
const RIPPLE_MS = 320;
/** Electric ticks off a sped-up body: cadence and life. */
const TICK_EVERY_MS = 95;
const TICK_LIFE_MS = 85;
const MAX_TICKS = 3;
/** Ice specks / drain ticks per second, before the tier's fxScale. */
const SPECK_PER_S = 4;
const DRAIN_PER_S = 7;
const EXHAUST_PUFF_EVERY_MS = 110;

const _world = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _q = new THREE.Quaternion();

/** The effect each slot draws. Order is draw order only; every present slot is drawn. */
const SLOTS = [
  ['shield', 'damage_reduction'],
  ['stun', 'stun'],
  ['slow', 'slow'],
  ['suppressed', 'suppress_abilities'],
  ['speed', 'speed'],
  ['faded', 'invisible'],
  ['hover', 'hover'],
];

function floorHexFor(group, colorHex) {
  const id = group.userData?.characterId;
  return id && CHARACTERS[id] ? telegraphHex(id) : colorHex;
}

function attach(group, mesh, x, y, z) {
  mesh.position.set(x, y, z);
  group.add(mesh);
  return mesh;
}

/** Parts that draw through the body (stars over the head) must not depth-test against it. */
function overBody(mesh) {
  mesh.material.depthTest = false;
  return mesh;
}

// ─── Slot builders: spawn(group, state, colorHex) fills state.parts ───────

const BUILD = {
  shield(group, s, colorHex) {
    const { mesh, u } = hexShield(colorHex);
    mesh.renderOrder = 10;
    s.shell = attach(group, mesh, 0, 0.08, 0);
    s.u = u;
    s.hitT = 0;
    s.parts.push(mesh);
  },
  stun(group, s) {
    s.stars = [];
    for (let i = 0; i < 3; i++) {
      const { mesh, u } = starFlash(STUN_HEX);
      mesh.renderOrder = 11;
      overBody(mesh);
      attach(group, mesh, 0, 0, 0);
      s.stars.push({ mesh, u });
      s.parts.push(mesh);
    }
    s.headY = (group.userData?.crownY ?? 1.25) + 0.05;
  },
  slow(group, s) {
    const { mesh, u } = iceCrust(SLOW_HEX);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 4;
    s.crust = attach(group, mesh, 0, FOOT_OFFSET + DECAL_LIFT, 0);
    s.u = u;
    s.acc = 0;
    s.parts.push(mesh);
  },
  suppressed(group, s) {
    const { mesh, u } = hazeRing(DRAIN_HEX);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 4;
    s.ring = attach(group, mesh, 0, FOOT_OFFSET + DECAL_LIFT, 0);
    s.u = u;
    s.acc = 0;
    s.parts.push(mesh);
  },
  speed(group, s, colorHex) {
    const { mesh, u } = hexRing(floorHexFor(group, colorHex));
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 4;
    s.glyph = attach(group, mesh, 0, FOOT_OFFSET + DECAL_LIFT, 0);
    s.u = u;
    s.ticks = [];
    s.nextTick = 0;
    s.parts.push(mesh);
  },
  faded(group, s, colorHex) {
    const { mesh, u } = hazeRing(colorHex);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 4;
    s.ring = attach(group, mesh, 0, FOOT_OFFSET + DECAL_LIFT, 0);
    s.u = u;
    s.parts.push(mesh);
  },
  hover(group, s, colorHex) {
    const { mesh, u } = column(colorHex);
    u.spin.value = Math.PI; // pointing down: an exhaust
    mesh.renderOrder = 9;
    s.flame = attach(group, mesh, 0, -0.75, 0);
    s.u = u;
    s.nextPuff = 0;
    s.parts.push(mesh);
  },
};

// ─── Per-frame animation: animate(group, state, now, dt, k) ───────────────
// `k` is the appearance scale (pop-in overshoot settling to 1); `endT` the
// erode-out progress when the slot is ending, else 0.

function spinFlat(mesh, group, angle) {
  // Undo the parent's yaw so a floor mark stays put while the body turns.
  mesh.rotation.z = -group.rotation.y + angle;
}

const ANIMATE = {
  shield(group, s, now, dt, k, endT) {
    s.shell.scale.set(1.05 * k, 1.12 * k, 1.05 * k);
    s.shell.rotation.y += dt * 0.35;
    if (s.hitT > 0) {
      s.hitT += dt * (1000 / RIPPLE_MS);
      if (s.hitT >= 1) s.hitT = 0;
      s.u.hit.value = s.hitT;
    }
    s.u.erode.value = endT;
  },
  stun(group, s, now, dt, k, endT) {
    const t = now / 1000;
    for (let i = 0; i < 3; i++) {
      const { mesh, u } = s.stars[i];
      const a = t * 4.6 + (i * Math.PI * 2) / 3;
      // Big enough to read from across the arena: the classic "seeing stars",
      // circling at FACE height and wide — above the crown they sat exactly
      // under the health bar and the name plate, which paint over them.
      mesh.position.set(Math.cos(a) * 0.8, s.headY - 0.42 + Math.sin(t * 6 + i) * 0.06, Math.sin(a) * 0.8);
      mesh.scale.setScalar(0.62 * k);
      u.spin.value += dt * 3;
      u.erode.value = endT;
    }
  },
  slow(group, s, now, dt, k, endT) {
    s.crust.scale.setScalar(1.55 * k);
    spinFlat(s.crust, group, now * 0.0004);
    s.u.erode.value = endT;
    if (endT === 0) {
      s.acc += dt * SPECK_PER_S * (getTierConfig().fxScale ?? 1);
      if (s.acc >= 1) {
        s.acc -= 1;
        group.getWorldPosition(_world);
        _world.y += FOOT_OFFSET + 0.3;
        fx.specks(_world, SLOW_HEX, 1, { up: 0.6, spread: 0.55, size: 0.14, life: 0.7 });
      }
    }
  },
  suppressed(group, s, now, dt, k, endT) {
    // A thin broken ring, not a puddle: the ticks rising off the body are the read.
    s.ring.scale.setScalar(0.95 * k);
    spinFlat(s.ring, group, 0);
    s.u.fade.value = 0.5;
    s.u.erode.value = endT;
    if (endT === 0) {
      s.acc += dt * DRAIN_PER_S * (getTierConfig().fxScale ?? 1);
      if (s.acc >= 1) {
        s.acc -= 1;
        group.getWorldPosition(_world);
        _world.y += 0.2;
        fx.specks(_world, DRAIN_HEX, 1, { up: 1.8, spread: 0.4, size: 0.22, life: 0.45, gain: 4 });
      }
    }
  },
  speed(group, s, now, dt, k, endT, colorHex) {
    s.glyph.scale.setScalar(1.05 * k);
    spinFlat(s.glyph, group, now * 0.0022);
    s.u.erode.value = endT;
    // Electric ticks: short-lived bolts flickering around the body.
    for (let i = s.ticks.length - 1; i >= 0; i--) {
      const tk = s.ticks[i];
      const t = (now - tk.born) / TICK_LIFE_MS;
      if (t >= 1) {
        releaseCelShape(tk.mesh);
        s.ticks.splice(i, 1);
        continue;
      }
      tk.u.fade.value = 1 - t * t;
    }
    if (endT === 0 && now >= s.nextTick && s.ticks.length < Math.max(1, Math.round(MAX_TICKS * (getTierConfig().fxScale ?? 1)))) {
      s.nextTick = now + TICK_EVERY_MS;
      const { mesh, u } = boltBeam(colorHex);
      const a = Math.random() * Math.PI * 2;
      const r = 0.55 + Math.random() * 0.25;
      attach(group, mesh, Math.cos(a) * r, -0.3 + Math.random() * 1.1, Math.sin(a) * r);
      mesh.rotation.set(Math.random() * 0.8 - 0.4, a + Math.PI / 2, Math.random() * Math.PI);
      mesh.scale.set(0.34, 0.55 + Math.random() * 0.5, 1);
      mesh.renderOrder = 10;
      overBody(mesh);
      s.ticks.push({ mesh, u, born: now });
    }
  },
  faded(group, s, now, dt, k, endT) {
    s.ring.scale.setScalar(1.1 * k);
    spinFlat(s.ring, group, 0);
    s.u.fade.value = 1;
    s.u.erode.value = endT;
  },
  hover(group, s, now, dt, k, endT, colorHex) {
    const flick = 1 + Math.sin(now * 0.031) * 0.08 + Math.sin(now * 0.017) * 0.05;
    s.flame.scale.set(1.25 * k, 2.3 * k * flick, 1);
    s.u.erode.value = endT;
    group.getWorldPosition(_world);
    const floorY = group.userData.hoverFloorY;
    if (Number.isFinite(floorY)) {
      // The blob shadow on the deck it took off from: the depth cue that says AIRBORNE.
      const h = Math.max(0, _world.y - PLAYER_HALF_HEIGHT - floorY);
      const k2 = Math.max(0.35, 1 - h / 10);
      fx.shadow(_world, floorY, 0.95 * k2 + 0.25, 0.55 * k2 + 0.1);
    }
    if (endT === 0 && now >= s.nextPuff) {
      s.nextPuff = now + EXHAUST_PUFF_EVERY_MS;
      _world.y -= 1.05;
      fx.puffs(_world, colorHex, 1, { up: -2.2, spread: 0.35, size: 0.26, life: 0.5, pale: Math.random() < 0.5, gravity: 1.5, floorY: Number.isFinite(floorY) ? floorY : undefined });
    }
  },
};

function releaseSlot(s) {
  for (const m of s.parts) releaseCelShape(m);
  s.parts.length = 0;
  if (s.ticks) {
    for (const tk of s.ticks) releaseCelShape(tk.mesh);
    s.ticks.length = 0;
  }
}

/**
 * Draws (and animates) every status on one character group. Called every
 * frame from both render paths — the local player and remote bodies go
 * through the identical call, so your own buffs read exactly as theirs do.
 *
 * @param {THREE.Object3D} group the character model group
 * @param {string[]} effects `PlayerSnapshot.effects`
 * @param {number} colorHex the character's own colour, for the identity-coloured slots
 */
export function setStatusAura(group, effects, colorHex) {
  const ud = group.userData;
  let auras = ud.auras;
  if (!auras) {
    // Nothing to draw and nothing drawn: the common case costs one lookup.
    if (!effects || effects.length === 0) return;
    auras = ud.auras = new Map();
    _bodies.add(group);
  }
  const now = performance.now();
  const dt = frame.delta;
  for (let i = 0; i < SLOTS.length; i++) {
    const [slot, effect] = SLOTS[i];
    const present = !!effects && effects.includes(effect);
    let s = auras.get(slot);
    if (!s) {
      if (!present) continue;
      s = { phase: 'live', born: now, endAt: 0, parts: [] };
      auras.set(slot, s);
      BUILD[slot](group, s, colorHex);
    }
    if (s.phase === 'live' && !present) {
      s.phase = 'ending';
      s.endAt = now;
    } else if (s.phase === 'ending' && present) {
      // Re-applied mid erode-out: it is whole again.
      s.phase = 'live';
      s.born = now - POP_MS;
    }
    let endT = 0;
    if (s.phase === 'ending') {
      endT = (now - s.endAt) / END_MS;
      if (endT >= 1) {
        releaseSlot(s);
        auras.delete(slot);
        continue;
      }
      endT = erosionAt(endT, 0);
    }
    const k = popScale(Math.min(1, (now - s.born) / POP_MS), 1, 0.35);
    ANIMATE[slot](group, s, now, dt, k, endT, colorHex);
  }
  if (auras.size === 0) {
    ud.auras = null;
    _bodies.delete(group);
  }
}

/**
 * The shield was struck at `point` (world): run the ripple over the shell
 * from there, plus the whole-shell flash. A body without a live shield
 * ignores it. Returns true when a shield took the hit.
 */
export function shieldHit(group, point) {
  const s = group?.userData?.auras?.get('shield');
  if (!s || s.phase !== 'live') return false;
  group.getWorldPosition(_world);
  _dir.set(point.x - _world.x, point.y - (_world.y + 0.08), point.z - _world.z);
  if (_dir.lengthSq() < 1e-6) _dir.set(0, 0, 1);
  // Into the shell's own frame: the group's yaw and the shell's slow spin.
  group.getWorldQuaternion(_q);
  _dir.applyQuaternion(_q.invert()).normalize();
  _dir.applyAxisAngle(THREE.Object3D.DEFAULT_UP, -s.shell.rotation.y);
  s.u.hitDir.value.copy(_dir);
  s.hitT = 0.001;
  s.u.hit.value = s.hitT;
  return true;
}

/** The deck a hovering body took off from, so its blob shadow lands on the right floor. */
export function setHoverFloor(group, floorY) {
  if (group) group.userData.hoverFloorY = floorY;
}

/** For tests: how many bodies the aura tracker currently holds. */
export function auraBodyCount() {
  return _bodies.size;
}

/** For tests and the probe: which slots are live on a body. */
export function statusAuraSlots(group) {
  const auras = group?.userData?.auras;
  return auras ? [...auras.entries()].map(([slot, s]) => `${slot}:${s.phase}`) : [];
}

/** Releases every aura shape on a body back to the pool. */
export function disposeStatusAura(group) {
  const auras = group?.userData?.auras;
  if (!auras) return;
  for (const s of auras.values()) releaseSlot(s);
  auras.clear();
  group.userData.auras = null;
  _bodies.delete(group);
}
